import { LiveAnnouncer } from '@angular/cdk/a11y';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { ComponentType, Overlay, OverlayConfig, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal, TemplatePortal } from '@angular/cdk/portal';
import {
  ComponentRef,
  EmbeddedViewRef,
  Inject,
  Injectable,
  InjectionToken,
  Injector,
  OnDestroy,
  Optional,
  SkipSelf,
  TemplateRef,
  Type,
} from '@angular/core';
import { takeUntil } from 'rxjs';

import { SimpleSnackBar, TextOnlySnackBar } from './simple-snack-bar';
import { NDT_SNACK_BAR_DATA, NdtSnackBarConfig } from './snack-bar-config';
import { NdtSnackBarContainer, NdtSnackBarContainerBase } from './snack-bar-container';
import { NdtSnackBarRef } from './snack-bar-ref';
import { NdtSnackBarModule } from './snack-bar.module';

/** @docs-private */
export function NDT_SNACK_BAR_DEFAULT_OPTIONS_FACTORY(): NdtSnackBarConfig {
  return new NdtSnackBarConfig();
}

/** Injection token that can be used to specify default snack bar. */
export const NDT_SNACK_BAR_DEFAULT_OPTIONS = new InjectionToken<NdtSnackBarConfig>('ndt-snack-bar-default-options', {
  providedIn: 'root',
  factory: NDT_SNACK_BAR_DEFAULT_OPTIONS_FACTORY,
});

@Injectable()
export abstract class NdtSnackBarBase implements OnDestroy {
  /**
   * Reference to the current snack bar in the view *at this level* (in the Angular injector tree).
   * If there is a parent snack-bar service, all operations should delegate to that parent
   * via `_openedSnackBarRef`.
   */
  private snackBarRefAtThisLevel: NdtSnackBarRef<any> | null = null;

  /** The component that should be rendered as the snack bar's simple component. */
  protected abstract simpleSnackBarComponent: Type<TextOnlySnackBar>;

  /** The container component that attaches the provided template or component. */
  protected abstract snackBarContainerComponent: Type<NdtSnackBarContainerBase>;

  /** The CSS class to apply for handset mode. */
  protected abstract handsetCssClass: string;

  constructor(
    private readonly overlay: Overlay,
    private readonly live: LiveAnnouncer,
    private readonly injector: Injector,
    private readonly breakpointObserver: BreakpointObserver,
    @Optional() @SkipSelf() private readonly parentSnackBar: NdtSnackBarBase,
    @Inject(NDT_SNACK_BAR_DEFAULT_OPTIONS) readonly defaultConfig: NdtSnackBarConfig,
  ) {}

  /** Reference to the currently opened snackbar at *any* level. */
  get openedSnackBarRef(): NdtSnackBarRef<any> | null {
    const parent = this.parentSnackBar;
    return parent ? parent.openedSnackBarRef : this.snackBarRefAtThisLevel;
  }

  set openedSnackBarRef(value: NdtSnackBarRef<any> | null) {
    if (this.parentSnackBar) {
      this.parentSnackBar.openedSnackBarRef = value;
    } else {
      this.snackBarRefAtThisLevel = value;
    }
  }

  /**
   * Creates and dispatches a snack bar with a custom component for the content, removing any
   * currently opened snack bars.
   * @param component Component to be instantiated.
   * @param config Extra configuration for the snack bar.
   */
  openFromComponent<T, D = any>(component: ComponentType<T>, config?: NdtSnackBarConfig<D>): NdtSnackBarRef<T> {
    return this.attach(component, config) as NdtSnackBarRef<T>;
  }

  /**
   * Creates and dispatches a snack bar with a custom template for the content, removing any
   * currently opened snack bars.
   * @param template Template to be instantiated.
   * @param config Extra configuration for the snack bar.
   */
  openFromTemplate(template: TemplateRef<any>, config?: NdtSnackBarConfig): NdtSnackBarRef<EmbeddedViewRef<any>> {
    return this.attach(template, config);
  }

  /**
   * Opens a snackbar with a message and an optional action.
   * @param message The message to show in the snackbar.
   * @param action The label for the snackbar action.
   * @param config Additional configuration options for the snackbar.
   */
  open(message: string, action = '', config?: NdtSnackBarConfig): NdtSnackBarRef<TextOnlySnackBar> {
    const cfg = { ...this.defaultConfig, ...config };

    // Since the user doesn't have access to the component, we can
    // override the data to pass in our own message and action.
    cfg.data = { message, action };

    // Since the snack bar has `role="alert"`, we don't
    // want to announce the same message twice.
    if (cfg.announcementMessage === message) {
      cfg.announcementMessage = undefined;
    }

    return this.openFromComponent(this.simpleSnackBarComponent, cfg);
  }

  /**
   * Dismisses the currently-visible snack bar.
   */
  dismiss(): void {
    if (this.openedSnackBarRef) {
      this.openedSnackBarRef.dismiss();
    }
  }

  ngOnDestroy(): void {
    // Only dismiss the snack bar at the current level on destroy.
    if (this.snackBarRefAtThisLevel) {
      this.snackBarRefAtThisLevel.dismiss();
    }
  }

  /**
   * Attaches the snack bar container component to the overlay.
   * @param overlayRef
   * @param config
   */
  private attachSnackBarContainer(overlayRef: OverlayRef, config: NdtSnackBarConfig): NdtSnackBarContainerBase {
    const userInjector = config && config.viewContainerRef && config.viewContainerRef.injector;
    const injector = Injector.create({
      parent: userInjector || this.injector,
      providers: [{ provide: NdtSnackBarConfig, useValue: config }],
    });

    const containerPortal = new ComponentPortal(this.snackBarContainerComponent, config.viewContainerRef, injector);
    const containerRef: ComponentRef<NdtSnackBarContainerBase> = overlayRef.attach(containerPortal);
    containerRef.instance.snackBarConfig = config;
    return containerRef.instance;
  }

  /**
   * Places a new component or a template as the content of the snack bar container.
   * @param content
   * @param userConfig
   */
  private attach<T>(
    content: ComponentType<T> | TemplateRef<T>,
    userConfig?: NdtSnackBarConfig,
  ): NdtSnackBarRef<T | EmbeddedViewRef<any>> {
    const config = { ...new NdtSnackBarConfig(), ...this.defaultConfig, ...userConfig };
    const overlayRef = this.reateOverlay(config);
    const container = this.attachSnackBarContainer(overlayRef, config);
    const snackBarRef = new NdtSnackBarRef<T | EmbeddedViewRef<any>>(container, overlayRef);

    if (content instanceof TemplateRef) {
      const portal = new TemplatePortal(content, null!, {
        $implicit: config.data,
        snackBarRef,
      } as any);

      snackBarRef.instance = container.attachTemplatePortal(portal);
    } else {
      const injector = this.createInjector(config, snackBarRef);
      const portal = new ComponentPortal(content, undefined, injector);
      const contentRef = container.attachComponentPortal<T>(portal);

      // We can't pass this via the injector, because the injector is created earlier.
      snackBarRef.instance = contentRef.instance;
    }

    // Subscribe to the breakpoint observer and attach the ndt-snack-bar-handset class as
    // appropriate. This class is applied to the overlay element because the overlay must expand to
    // fill the width of the screen for full width snackbars.
    this.breakpointObserver
      .observe(Breakpoints.HandsetPortrait)
      .pipe(takeUntil(overlayRef.detachments()))
      .subscribe((state) => {
        overlayRef.overlayElement.classList.toggle(this.handsetCssClass, state.matches);
      });

    if (config.announcementMessage) {
      // Wait until the snack bar contents have been announced then deliver this message.
      container.onAnnounce.subscribe(() => {
        void this.live.announce(config.announcementMessage, config.politeness);
      });
    }

    this.animateSnackBar(snackBarRef, config);
    this.openedSnackBarRef = snackBarRef;
    return this.openedSnackBarRef;
  }

  /**
   * Animates the old snack bar out and the new one in.
   * @param snackBarRef
   * @param config
   */
  private animateSnackBar(snackBarRef: NdtSnackBarRef<any>, config: NdtSnackBarConfig): void {
    // When the snackbar is dismissed, clear the reference to it.
    snackBarRef.afterDismissed().subscribe(() => {
      // Clear the snackbar ref if it hasn't already been replaced by a newer snackbar.
      if (this.openedSnackBarRef === snackBarRef) {
        this.openedSnackBarRef = null;
      }

      if (config.announcementMessage) {
        this.live.clear();
      }
    });

    if (this.openedSnackBarRef) {
      // If a snack bar is already in view, dismiss it and enter the
      // new snack bar after exit animation is complete.
      this.openedSnackBarRef.afterDismissed().subscribe(() => {
        snackBarRef.containerInstance.enter();
      });
      this.openedSnackBarRef.dismiss();
    } else {
      // If no snack bar is in view, enter the new snack bar.
      snackBarRef.containerInstance.enter();
    }

    // If a dismiss timeout is provided, set up dismiss based on after the snackbar is opened.
    if (config.duration && config.duration > 0) {
      snackBarRef.afterOpened().subscribe(() => snackBarRef.dismissAfter(config.duration));
    }
  }

  /**
   * Creates a new overlay and places it in the correct location.
   * @param config The user-specified snack bar config.
   */
  private reateOverlay(config: NdtSnackBarConfig): OverlayRef {
    const overlayConfig = new OverlayConfig();
    overlayConfig.direction = config.direction;

    const positionStrategy = this.overlay.position().global();
    // Set horizontal position.
    const isRtl = config.direction === 'rtl';
    const isLeft =
      config.horizontalPosition === 'left' ||
      (config.horizontalPosition === 'start' && !isRtl) ||
      (config.horizontalPosition === 'end' && isRtl);
    const isRight = !isLeft && config.horizontalPosition !== 'center';
    if (isLeft) {
      positionStrategy.left('0');
    } else if (isRight) {
      positionStrategy.right('0');
    } else {
      positionStrategy.centerHorizontally();
    }
    // Set horizontal position.
    if (config.verticalPosition === 'top') {
      positionStrategy.top('0');
    } else {
      positionStrategy.bottom('0');
    }

    overlayConfig.positionStrategy = positionStrategy;
    return this.overlay.create(overlayConfig);
  }

  /**
   * Creates an injector to be used inside of a snack bar component.
   * @param config Config that was used to create the snack bar.
   * @param snackBarRef Reference to the snack bar.
   */
  private createInjector<T>(config: NdtSnackBarConfig, snackBarRef: NdtSnackBarRef<T>): Injector {
    const userInjector = config && config.viewContainerRef && config.viewContainerRef.injector;

    return Injector.create({
      parent: userInjector || this.injector,
      providers: [
        { provide: NdtSnackBarRef, useValue: snackBarRef },
        { provide: NDT_SNACK_BAR_DATA, useValue: config.data },
      ],
    });
  }
}

/**
 * Service to dispatch nDziekanat Design System snack bar messages.
 */
@Injectable({ providedIn: NdtSnackBarModule })
export class NdtSnackBar extends NdtSnackBarBase {
  protected override simpleSnackBarComponent = SimpleSnackBar;
  protected override snackBarContainerComponent = NdtSnackBarContainer;
  protected override handsetCssClass = 'ndt-mdc-snack-bar-handset';

  constructor(
    overlay: Overlay,
    live: LiveAnnouncer,
    injector: Injector,
    breakpointObserver: BreakpointObserver,
    @Optional() @SkipSelf() parentSnackBar: NdtSnackBar,
    @Inject(NDT_SNACK_BAR_DEFAULT_OPTIONS) override readonly defaultConfig: NdtSnackBarConfig,
  ) {
    super(overlay, live, injector, breakpointObserver, parentSnackBar, defaultConfig);
  }
}
